import moment from "moment";
import errorAudio from "../assets/audio/error.mp3";
import successAudio from "../assets/audio/success.mp3";


// Targeted imports for major icon libraries
import * as FaIcons from "react-icons/fa";
import * as BiIcons from "react-icons/bi";
import * as Io5Icons from "react-icons/io5";

import React from "react";
import IconsList from "./IconList";

/**
 * Curated mapping for GetIcon to avoid importing entire libraries
 * This covers common terms used throughout the application
 */
const ICON_MAP = {
  // Common terms
  user: FaIcons.FaUser,
  users: FaIcons.FaUsers,
  ticket: FaIcons.FaTicketAlt,
  tickets: FaIcons.FaTicketAlt,
  invoice: FaIcons.FaFileInvoice,
  home: FaIcons.FaHome,
  dollar: FaIcons.FaDollarSign,
  price: FaIcons.FaTags,
  time: FaIcons.FaClock,
  tools: FaIcons.FaTools,
  whatsapp: FaIcons.FaWhatsapp,
  edit: FaIcons.FaEdit,
  plus: FaIcons.FaPlus,
  trash: FaIcons.FaTrash,
  pc: Io5Icons.IoDesktop,
  desktop: Io5Icons.IoDesktop,
  mobile: FaIcons.FaMobileAlt,
  apple: FaIcons.FaApple,
  android: FaIcons.FaAndroid,
  samsung: FaIcons.FaMobileAlt,

  // Specific library prefixes
  biuser: BiIcons.BiUser,
};

export function sumDurations(durations) {
  let totalDuration = moment.duration(0);
  console.log("durations sumDUrations ", durations);
  durations.forEach((duration) => {
    const [days, hours, minutes] = duration.split(":").map(Number);
    totalDuration.add(days, "days");
    totalDuration.add(hours, "hours");
    totalDuration.add(minutes, "minutes");
  });

  const totalDays = totalDuration.days();
  const totalHours = totalDuration.hours();
  const totalMinutes = totalDuration.minutes();

  return `${totalDays.toString().padStart(2, "0")}:${totalHours
    .toString()
    .padStart(2, "0")}:${totalMinutes.toString().padStart(2, "0")}`;
}

const similarityRatio = (str1, str2) => {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;
  const longerLength = longer.length;
  if (longerLength === 0) return 1.0;
  return (longerLength - editDistance(longer, shorter)) / parseFloat(longerLength);
};

const editDistance = (str1, str2) => {
  const costs = [];
  for (let i = 0; i <= str1.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= str2.length; j++) {
      if (i === 0) {
        costs[j] = j;
      } else {
        if (j > 0) {
          let newValue = costs[j - 1];
          if (str1.charAt(i - 1) !== str2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
    }
    if (i > 0) costs[str2.length] = lastValue;
  }
  return costs[str2.length];
};

/**
 * Returns an icon component based on the provided text.
 * Refactored to use a curated map to avoid massive bundle bloat.
 *
 * @param {string} text - The text to search for an icon.
 * @param {boolean} avatar - Whether to wrap in an avatar span.
 * @returns {JSX.Element} The icon component if found, otherwise a default icon.
 */
export const GetIcon = (text = "pc", avatar = false) => {
  if (!text) return avatar ? <span className="bg-light text-black rounded-circle p-1 me-1"><Io5Icons.IoDesktop /></span> : <Io5Icons.IoDesktop />;

  const iconName = text.toLowerCase().trim().split(/\s+/)[0];

  // 1. Check curated mapping first
  const MappedIcon = ICON_MAP[iconName];
  if (MappedIcon) {
    return avatar ? (
      <span className="bg-light text-black rounded-circle p-1 me-1" style={{ maxWidth: 20, maxHeight: 20 }}>
        <MappedIcon style={{ color: 'inherit' }} />
      </span>
    ) : (
      <MappedIcon style={{ fontSize: 'inherit' }} />
    );
  }

  // 2. Check IconsList (curated list for repair/brand categories)
  const ic = IconsList.find((x) => x.name.toLowerCase().includes(iconName));
  if (ic) {
    return avatar ? (
      <span className="bg-light text-black rounded-circle p-1 me-1" style={{ maxWidth: 20, maxHeight: 20 }}>
        <ic.icon style={{ color: 'inherit' }} />
      </span>
    ) : (
      <ic.icon style={{ fontSize: 'inherit' }} />
    );
  }

  // 3. Last resort - fallback or similarity check on IconsList
  let bestMatch;
  let highestSimilarity = 0;

  IconsList.forEach((item) => {
    const similarity = similarityRatio(iconName, item.name.toLowerCase());
    if (similarity > highestSimilarity) {
      highestSimilarity = similarity;
      bestMatch = item.icon;
    }
  });

  const FinalIcon = highestSimilarity > 0.5 ? bestMatch : Io5Icons.IoDesktop;

  return avatar ? (
    <span className="bg-light text-black rounded-circle p-1 me-1" style={{ maxWidth: 20, maxHeight: 20 }}>
      <FinalIcon style={{ color: 'inherit' }} />
    </span>
  ) : (
    <FinalIcon style={{ fontSize: 'inherit' }} />
  );
};

// ============= Constants =============
const CURRENCY_LOCALE = "en-IN";
const DATE_FORMAT = "DD/MM/YYYY";
const TIME_FORMAT = "hh:mm:ss a";
const NUMBER_ARRAYS = {
  units: [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
  ],
  teens: [
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ],
  tens: [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ],
  thousands: [
    "",
    "Thousand",
    "Million",
    "Billion",
    "Trillion",
    "Quadrillion",
    "Quintillion",
    "Sextillion",
  ],
};

// ============= Currency/Number Formatting =============
export const formatCurrency = (value) => {
  if (value === null || value === undefined) return "Rs. 0.00";
  const num = Number(value) || 0;
  return `Rs. ${new Intl.NumberFormat(CURRENCY_LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num)}`;
};

export const roundTo = (number, decimals = 2) =>
  Number(Math.round(number + "e" + decimals) + "e-" + decimals);

export const calculatePercentage = (value, total) => (value / total) * 100;

// ============= Date/Time Utilities =============
export const formatDate = (date) => moment(date).format(DATE_FORMAT);

export const formatTime = (value) => moment(value).format(TIME_FORMAT);

export const formatDateTime = (date) =>
  moment(date).format(`${DATE_FORMAT} ${TIME_FORMAT}`);

export const getDateDifference = (date1, date2, unit = "days") =>
  moment(date1).diff(moment(date2), unit);

export const addDays = (date, days) => moment(date).add(days, "days").toDate();

// ============= String Utilities =============
export const truncateText = (text, maxLength) =>
  text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;

export const capitalize = (str) =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

export const slugify = (text) =>
  text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");

// ============= Number to Words =============
export const numberToWords = (num = 0) => {
  if (num === 0) return "Zero";

  const convert = (n) => {
    if (n < 10) return NUMBER_ARRAYS.units[n];
    if (n < 20) return NUMBER_ARRAYS.teens[n - 10];
    if (n < 100)
      return `${NUMBER_ARRAYS.tens[Math.floor(n / 10)]} ${NUMBER_ARRAYS.units[n % 10]
        }`.trim();
    if (n < 1000)
      return `${NUMBER_ARRAYS.units[Math.floor(n / 100)]} Hundred ${convert(
        n % 100
      )}`.trim();
    for (let i = 0; i < NUMBER_ARRAYS.thousands.length; i++) {
      const unit = 1000 ** (i + 1);
      if (n < unit)
        return `${convert(Math.floor(n / (unit / 1000)))} ${NUMBER_ARRAYS.thousands[i]
          } ${convert(n % (unit / 1000))}`.trim();
    }
  };

  const [integerPart, decimalPart] = num.toString().split(".");
  const words = convert(Number(integerPart));

  if (decimalPart) {
    const decimalWords = decimalPart
      .split("")
      .map((digit) => NUMBER_ARRAYS.units[Number(digit)])
      .join(" ");
    return `${words} and ${decimalWords} Cents`;
  }
  return words;
};

// ============= Audio Feedback =============
// SINGLETON PATTERN: Load audio once, reuse forever
const successAudioObj = new Audio(successAudio);
const errorAudioObj = new Audio(errorAudio);
successAudioObj.preload = "auto";
errorAudioObj.preload = "auto";

export const PlaySuccess = async () => {
  try {
    successAudioObj.currentTime = 0; // Reset to start
    await successAudioObj.play().catch(() => { }); // Silent fail if browser blocks autoplay
    return true;
  } catch (error) {
    console.error("Error playing success sound:", error);
    return false;
  }
};

export const PlayError = async () => {
  try {
    errorAudioObj.currentTime = 0; // Reset to start
    await errorAudioObj.play().catch(() => { }); // Silent fail if browser blocks autoplay
    return true;
  } catch (error) {
    console.error("Error playing error sound:", error);
    return false;
  }
};

// ============= Storage Utilities =============
export const storage = {
  set: (key, value) => localStorage.setItem(key, JSON.stringify(value)),
  get: (key) => {
    try {
      return JSON.parse(localStorage.getItem(key));
    } catch {
      return null;
    }
  },
  remove: (key) => localStorage.removeItem(key),
  clear: () => localStorage.clear(),
};

// ============= Validation Utilities =============
export const validation = {
  isEmail: (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
  isPhone: (phone) => /^\+?[\d\s-]{10,}$/.test(phone),
  isURL: (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  },
};

// ============= Array/Object Utilities =============
export const arrayUtils = {
  unique: (arr) => [...new Set(arr)],
  groupBy: (array, key) =>
    array.reduce(
      (result, item) => ({
        ...result,
        [item[key]]: [...(result[item[key]] || []), item],
      }),
      {}
    ),
  sum: (arr) => arr.reduce((a, b) => a + b, 0),
  average: (arr) => arr.reduce((a, b) => a + b, 0) / arr.length,
};

// ============= Browser/Device Utilities =============
export const browser = {
  copyToClipboard: async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  },
  isMobile: () =>
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ),
  downloadFile: (url, filename) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },
};

export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};
